/**
 * @file ShipmentCardActions.tsx
 * @description Блок кнопок в карточке отгрузки (phase19.12).
 *
 * Вынесен из OrderShipmentCard: набор действий зависит от состояния отгрузки
 * (черновик / передана перевозчику), и вместе с ним карточка перестала
 * помещаться в лимит 200 строк.
 */

import { Calculator, Package, Truck } from "lucide-react";

import { ShipmentDeleteAction } from "./ShipmentDeleteAction";
import { ShipmentPrintActions } from "./ShipmentPrintActions";
import { ShipmentTrackEditor } from "./ShipmentTrackEditor";
import { Button } from "../ui/button";
import type { ShipmentPrintForm } from "../../lib/api-carriers-sdek";
import { isManualTrackCarrier, type ShipmentDto } from "../../lib/api-orders";

interface Props {
  shipment: ShipmentDto;
  /** Отгрузка уже у перевозчика — тариф и места менять нельзя. */
  handed: boolean;
  acting: boolean;
  printing?: ShipmentPrintForm | null;
  onQuote: () => void;
  onPickPoint: () => void;
  onSendToSdek: () => void;
  onSyncSdek: () => void;
  onPrint: (form: ShipmentPrintForm) => void;
  onShip: () => void;
  onDeliver: () => void;
  onDelete: (onDone: () => void) => void;
  /** Ручной трек-номер (перевозчики без интеграции, phase19.15). */
  onSaveTrack: (trackNumber: string) => void;
}

export function ShipmentCardActions({
  shipment: s,
  handed,
  acting,
  printing,
  onQuote,
  onPickPoint,
  onSendToSdek,
  onSyncSdek,
  onPrint,
  onShip,
  onDeliver,
  onDelete,
  onSaveTrack,
}: Props) {
  const isSdek = s.carrier === "SDEK";
  const noTariff = s.tariffCode === null;
  const editable = !handed;

  return (
    // На 360px блок действий уходит на свою строку — иначе кнопки вылезают
    // за край карточки.
    <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">
      {isSdek && editable ? (
        <>
          <Button
            size="sm"
            variant="outline"
            className="min-h-11"
            onClick={onQuote}
            disabled={acting}
          >
            <Calculator className="mr-1 size-4" />
            Рассчитать
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="min-h-11"
            onClick={onPickPoint}
            disabled={acting}
          >
            <Package className="mr-1 size-4" />
            {s.pickupPointCode ? "Сменить ПВЗ" : "Выбрать ПВЗ"}
          </Button>
          <Button
            size="sm"
            className="min-h-11"
            onClick={onSendToSdek}
            disabled={acting || noTariff}
            title={
              noTariff
                ? "Сначала рассчитайте доставку — без тарифа заявка уйдёт с параметрами по умолчанию"
                : undefined
            }
          >
            <Truck className="mr-1 size-4" />
            Передать в СДЭК
          </Button>
        </>
      ) : null}
      {isSdek && handed ? (
        <ShipmentPrintActions
          printing={printing ?? null}
          acting={acting}
          awaitingTrack={!s.trackNumber}
          onSync={onSyncSdek}
          onPrint={onPrint}
        />
      ) : null}
      {/* Трек у ручного перевозчика правим на любом статусе: номер часто
          приносят уже после отгрузки, а клиент ждёт его в ЛК. */}
      {isManualTrackCarrier(s.carrier) ? (
        <ShipmentTrackEditor
          trackNumber={s.trackNumber}
          acting={acting}
          onSave={onSaveTrack}
        />
      ) : null}
      {s.status === "PENDING" && !isSdek ? (
        <Button size="sm" className="min-h-11" onClick={onShip} disabled={acting}>
          Отгрузить
        </Button>
      ) : null}
      {s.status === "IN_TRANSIT" ? (
        <Button size="sm" className="min-h-11" onClick={onDeliver} disabled={acting}>
          Доставлено
        </Button>
      ) : null}
      {/* Удалить можно только черновик — у переданной отгрузки кнопки нет. */}
      {editable ? <ShipmentDeleteAction acting={acting} onDelete={onDelete} /> : null}
    </div>
  );
}
